'use client'
import { type FC, type Dispatch, type SetStateAction, useMemo, useCallback } from 'react'
import { useRouter, usePathname, useParams } from 'next/navigation'
import { Link } from './Link'
import styled from 'styled-components'
import { motion } from 'motion/react'
import type { AnimationGeneratorType, Easing, Variants } from 'motion/react'
import { useDeviceType, useSession, useTranslator } from '@/hooks'
import { mq, getNavbarLink10, getP12, getP10, rgbAlpha } from '@/styles'
import { navLinksByRole, iconByName, cookie, DY_COOKIE_SESSION } from '@/utils'
import { Figure } from '.'

type Props = {
  navCollapsed: boolean
  setNavCollapsed: Dispatch<SetStateAction<boolean>>
}

const icons = {
  orders: 'invoices',
  commissions: 'invoices',
  users: 'dashboard',
  vet_corporate_users: 'customers'
}

const getListItems = (user, t, pathname, id) => {
  const linksObjects = navLinksByRole[user.role]
  if (!linksObjects) return

  const vets = user.vets
  const vet_id = id ?? vets?.[0]?.id

  return linksObjects.map((item) => {
    const iconName = item.replace('myvet_', '')
    if (user.role === 'zone_manager' && item === 'vet_corporate_users' && (!vets || vets?.length === 1)) return false
    const multiVet = (item === 'myvet_clinic' || item === 'vets') && vets?.length > 1

    if(item.includes('myvet_') && item !== 'myvet_clinic' && !id) return false
    const icon = icons[iconName] ?? iconName
    const href = (user.role !== 'zone_manager' && multiVet) ? '/my-vets' : `${item.includes('myvet_') ? `/my-vet/${vet_id}` : ''}${item === 'myvet_clinic' ? '' : t(`layout.nav.${item}.href`)}`

    const name = multiVet ? 'Mis clínicas' : t(`layout.nav.${item}.name`)

    return (
      <li key={item} {...(item === 'myvet_clinic' || (item === 'vets' && (user.role === 'admin' || user.role === 'zone_manager'))) && { className: 'your-vet-clinic' }}>
        <Link href={href} className={(pathname === href) ? 'active' : ''} prefetch={false}>
          <Figure media={iconByName(icon)} />
          <span>{name}</span>
        </Link>
        {multiVet && Array.isArray(vets) && id &&  <ul>
          {vets.filter(vet => (vet.id + '') === (vet_id + ''))?.map(vet =>
            <li key={vet.id} data-id={vet.id}>
              <Link href={`${(user.role === 'admin' || user.role === 'zone_manager') ? '/vet' : '/my-vet'}/${vet.id}`} className={(id + '') === (vet.id + '') ? 'active' : ''} prefetch={false}>
                <span>{vet.comercial_name}</span>
              </Link>
            </li>
          )}
          {/* {vets.map(vet =>
            <li key={vet.id} data-id={vet.id}>
              <Link href={`/my-vet/${vet.id}`} className={(vet_id + '') === (vet.id + '') ? 'active' : ''} prefetch={false}>
                <span>{vet.comercial_name}</span>
              </Link>
            </li>
          )} */}
        </ul>}
      </li>
    )
  })
}

const getInitials = (fullname) => {
  if(!fullname) return 'U'
  const splittedFullName = fullname.split(' ')
  return `${splittedFullName[0][0]}${splittedFullName[1]?.[0] ?? ''}`
}

export const Nav:FC<Props> = ({ navCollapsed, setNavCollapsed}) => {
  const { isMobile } = useDeviceType()
  const animate = navCollapsed ? 'collapsed' : isMobile ? 'expandedMobile' : 'expanded'
  const pathname = usePathname()
  const { vet_id } = useParams()
  const { push } = useRouter()
  const { user, setUser } = useSession()

  const { t } = useTranslator()
  const Items = useMemo(() => !user ? [] : getListItems(user, t, pathname, vet_id), [user, t, pathname, vet_id])

  const onClickLogout = useCallback(() => {
    localStorage.removeItem(`dy_virbac_user_${user.id}`)
    cookie.remove(DY_COOKIE_SESSION)
    setUser(null)
    push('/login')
  }, [setUser, push, user])

  return (
    <>
      <NavStyled variants={variants} initial={'expanded'} animate={animate} $collapsed={navCollapsed} $myVetPathname={(pathname.includes('/my-vet/') || pathname.includes('/vet/')) && !!vet_id}>
        <div className='logo-wrapper'>
          <button onClick={() => { setNavCollapsed(prevState => !prevState)}}>
            <img src='/images/svg/icon-burger_chevron.svg' alt='burger' />
            <img src='/images/svg/icon-burger.svg' alt='burger' />
          </button>
          <Link href='/'>
            <motion.img src='/images/svg/logo-white.svg' alt='Virbac logo' variants={logoVariants} initial={'expanded'} animate={navCollapsed ? 'collapsed' : 'expanded'} />
          </Link>
        </div>
        <ul>
          {Items}
          {user?.vets?.length > 1 && !vet_id &&
            <li>
              <Link href={'/my-clients'} {...pathname === '/my-clients' && { className: 'active' }} prefetch={false}>
                <Figure media={iconByName('customers')} />
                <span>Mis clientes</span>
              </Link>
            </li>
          }
          {user &&
            <>
              <li className='account'>
                <Link href='/account' className={pathname === '/account' ? 'active' : ''}>
                  <abbr>{getInitials(user?.name)}</abbr>
                  <span>{user?.name ?? 'User'}</span>
                </Link>
              </li>
              <li className='logout'>
                <button onClick={onClickLogout}>
                  <Figure media={iconByName('logout')} />
                  <span>{t('commons.actions.logout')}</span>
                </button>
              </li>
            </>
          }
          <li className='legal'>
            <Link href='/politica-de-privacidad' className={(pathname === 'politica-de-privacidad' || pathname === 'rgpd' || pathname === 'condiciones-de-uso' || pathname === 'acuerdo-comercial') ? 'active' : ''}>
              <Figure media={iconByName('legal')} />
              <span>Términos legales</span>
            </Link>
          </li>
        </ul>
      </NavStyled>
      <button className='backdrop' data-collapsed={navCollapsed} onClick={() => { setNavCollapsed(prevState => !prevState) }}></button>
    </>
  )
}

const NavStyled = styled(motion.nav) <{ $collapsed: boolean, $myVetPathname }>`
  background: ${({ $myVetPathname }) => $myVetPathname ? 'var(--color-navbar)' : 'var(--color-brown)'};
  position: fixed;
  height: 60px;
  overflow-x: hidden;
  padding: 0;
  top: 0;
  transition: 200ms background ease-out;
  width: 50px;
  z-index: 20;

  ~ .page-wrapper {
    margin-left: 0;
  }

  ${mq.greaterThan('tablet')} {
    height: 100vh;
    /* padding-bottom: 60px; */
  }

  ul {
    background: ${({ $myVetPathname }) => $myVetPathname ? 'var(--color-navbar)' : 'var(--color-brown)'};
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    height: 100vh;
    left: 0;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 80px 0 0;
    position: fixed;
    top: 0;
    transform: translateX(${({ $collapsed }) => $collapsed ? '-250px' : 0});
    transition: 200ms transform ease-out, 200ms background ease-out;
    width: 250px;

    ${mq.greaterThan('tablet')} {
      height: 100%;
      left: auto;
      max-height: calc(100vh - 80px);
      padding: 0;
      position: relative;
      top: auto;
      transform: none;
      width: 100%;
    }

    ul {
      height: fit-content;
      margin-left: auto;
      position: relative;
      width: 90%;

      li {
        margin-bottom: 0;
      }
    }
  }

  li {
    display: block;
    margin-bottom: 5px;
    width: 100%;

    &.your-vet-clinic {
      margin-bottom: 20px;

      /* &:after {
        transform: translate(20px, 0px);
        background-color: rgba(255, 255, 255, .1);
        content: '';
        display: block;
        height: 1px;
        width: calc(100% - 20px);
      } */
      ul {
        a {
          border-bottom-left-radius: 30px;
          border-top-left-radius: 30px;

          &.active {
            background: ${rgbAlpha('--color-white', .25)};
          }
        }
      }
    }

    &.account {
      margin-top: auto;

      a {
        padding-left: 15px;
      }
    }

    &.legal {
      background-color: ${rgbAlpha('--color-black', .2)};
      margin-bottom: 0;

      figure {
        opacity: ${({ $collapsed }) => $collapsed ? 1 : 0};
      }

      span {
        ${getP10()}
        font-size: 12px;
        text-transform: capitalize;
        color: rgba(255,255,255, .55);
      }
    }

    button,
    a {
      ${getP12()}
      letter-spacing: 1.2px;
      align-items: center;
      color: white;
      display: flex;
      height: 42px;
      opacity: .85;
      padding: 0 20px;
      text-decoration: none;
      transition: 300ms background-color ease-in-out, 300ms opacity ease-in-out;
      white-space: nowrap;
      width: 100%;

      ${mq.greaterThan('tablet')} {
        ${getNavbarLink10()}
      }

      span {
        margin-left: 15px;
        opacity: 1;
        overflow: hidden;
        pointer-events: auto;
        text-overflow: ellipsis;
        transition: 200ms opacity ease-out;
        white-space: nowrap;
        user-select: auto;

        ${mq.greaterThan('tablet')} {
          opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
          pointer-events: ${({ $collapsed }) => $collapsed ? 'none' : 'auto'};
          user-select: ${({ $collapsed }) => $collapsed ? 'none' : 'auto'};
        }
      }

      figure {
        align-items: center;
        display: flex;
        flex-shrink: 0;
        height: 14px;
        justify-content: center;
        margin: 0 3px;
        text-align: center;
        width: 14px;
      }

      img {
        object-fit: contain;
      }

      &.active,
      &:hover {
        background: ${rgbAlpha('--color-black', .4)};
        /* background: ${({ $myVetPathname }) => rgbAlpha($myVetPathname ? '--color-blue' : '--color-brown', .4)}; */
        opacity: .99;
      }
    }
  }

  abbr {
    align-items: center;
    background: ${rgbAlpha('--color-black', .2)};
    border-radius: 50%;
    display: flex;
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    height: 30px;
    justify-content: center;
    line-height: 12px;
    min-width: 30px;
    padding-top: 1px;
    text-transform: uppercase;
    width: 30px;
  }


  .logo-wrapper {
    background: ${({ $myVetPathname }) => $myVetPathname ? 'var(--color-navbar)' : 'var(--color-brown)'};
    align-items: center;
    display: flex;
    height: 60px;
    justify-content: space-between;
    margin-bottom: 20px;
    overflow-x: hidden;
    position: relative;
    transition: 100ms height ease-out;
    z-index: 60;

    a {
      padding: 0 20px;

      > img {
        /* display: ${({ $collapsed }) => $collapsed ? 'none' : 'inline-block'}; */
        margin-top: 2px;
        width: 80px;

        ${mq.greaterThan('tablet')} {
          margin-top: 0;
        }
      }
    }

    button {
      align-items: center;
      background: transparent;
      border: 0;
      display: flex;
      flex-shrink: 0;
      height: 100%;
      outline: 0;
      padding: 0 20px;
      transition: 300ms background-color ease-in-out;

      &:hover {
        background: ${rgbAlpha('--color-white', .2)};
      }

      > img {
        &:first-child {
          transition: 200ms transform ease-out;
          transform: rotate(${({ $collapsed }) => $collapsed ? '180deg' : 0});
        }

        &:last-child {
          margin-left: 2px;
        }
      }
    }
  }

  + .backdrop {
    background: rgba(255, 255, 255, 0);
    border: 0;
    display: block;
    height: 100vh;
    margin: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: calc(100vw - 250px);
    z-index: 20;

    &[data-collapsed='true'] {
      display: none;
      pointer-events: none;
      user-select: none;
    }

    ${mq.greaterThan('tablet')} {
      display: none;
      pointer-events: none;
      user-select: none;
    }
  }
`

const transition = {
  duration: .15,
  type: 'tween' as AnimationGeneratorType,
  ease: 'easeOut' as Easing,
  when: 'afterChildren',
}

const variants:Variants = {
  collapsed: {
    width: '60px',
    transition
  },
  expandedMobile: {
    width: '250px',
    transition
  },
  expanded: {
    width: '200px',
    transition
  }
}

const logoTransition = {
  duration: .2,
  type: 'tween' as AnimationGeneratorType,
  ease: 'easeOut' as Easing,
}

const logoVariants:Variants = {
  collapsed: {
    opacity: 0,
    transition: logoTransition
  },
  expanded: {
    opacity: 1,
    transition: { ...logoTransition, ...{ delay: .10 } }
  }
}
